import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Typography, Paper, Divider, 
  List, ListItem, ListItemText, ListItemAvatar, ListItemSecondaryAction,
  Avatar, Chip, TextField, InputAdornment, Badge,
  CircularProgress, Pagination, Tab, Tabs, useTheme, alpha,
  Alert, Snackbar, Button, IconButton, Tooltip, Grid,
  Card, CardContent, Skeleton, Grow, Fade, Zoom
} from '@mui/material';
import {
  Email as EmailIcon,
  Search as SearchIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Campaign as CampaignIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  DoneAll as DoneAllIcon,
  Dashboard as DashboardIcon,
  FilterList as FilterListIcon,
  Send as SendIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { formatDistance } from 'date-fns';
import messageService from '../../../services/messageService';
import { useNotification } from '../../../contexts/NotificationContext';
import { useAuth } from '../../../contexts/AuthContext';
import MessageDialog from './MessageDialog';
import { motion } from 'framer-motion';

const AdminMessagesPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const notification = useNotification();
  const { user } = useAuth();
  const location = useLocation();
  const isStaffPortal = location.pathname.startsWith('/staff/') || user?.role === 'staff';
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  const showLocalNotification = (message, severity = 'info') => {
    if (notification) {
      if (typeof notification.notify === 'function') {
        notification.notify(message, severity);
        return;
      } else if (typeof notification.showNotification === 'function') {
        notification.showNotification(message, severity);
        return;
      } else if (typeof notification.setNotification === 'function') {
        notification.setNotification({ message, severity, open: true });
        return;
      }
    }
    
    setSnackbar({
      open: true,
      message,
      severity
    });
  };
  
  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };
  
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    broadcast: 0,
    direct: 0,
    unread: 0,
    today: 0
  });
  const [refreshing, setRefreshing] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);

  // Animation states
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Add an animation delay for a better UX
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 300);
    return () => clearTimeout(timer);
  }, []);
  
  const fetchMessages = async () => {
    setLoading(true);
    try {
      let response;
      
      // Use different API endpoints based on filter
      if (filter === 'sent') {
        response = await messageService.getAdminSentMessages(page, 10);
      } else {
        response = await messageService.getAdminMessages(page, 10, filter);
      }
      
      if (response.success) {
        setMessages(response.data || []);
        setTotalPages(response.totalPages || 1);
      } else {
        setError(response.error || 'Failed to fetch messages');
        showLocalNotification('Failed to load messages', 'error');
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
      setError('An error occurred while fetching messages');
      showLocalNotification('Error loading messages', 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await messageService.getMessageStats();
      if (response.success) {
        // Make sure we're correctly extracting the stats data from the response
        setStats({
          total: response.data?.total || 0,
          broadcast: response.data?.broadcast || 0,
          direct: response.data?.direct || 0,
          unread: response.data?.unread || 0,
          today: response.data?.today || 0,
          sent: response.data?.sent || 0
        });
        
        // Log the stats for debugging
        console.log('Message stats updated:', response.data);
      } else {
        console.error('Failed to fetch message stats:', response.error);
      }
    } catch (err) {
      console.error('Error fetching message stats:', err);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchMessages(), fetchStats()]);
    setRefreshing(false);
  };
  
  useEffect(() => {
    fetchMessages();
    fetchStats();
  }, [page, filter]);
  
  useEffect(() => {
    if (searchTerm) {
      const timer = setTimeout(() => {
        const filteredMessages = messages.filter(message => 
          message.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          message.text?.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setMessages(filteredMessages);
      }, 300); // Debounce search
      return () => clearTimeout(timer);
    } else {
      // If search is cleared, refetch the messages
      fetchMessages();
    }
  }, [searchTerm]);
  
  const handleFilterChange = (event, newValue) => {
    setFilter(newValue);
    setPage(1); // Reset to first page when changing filters
  };
  
  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when changing pages
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleOpenMessage = async (messageId) => {
    setSelectedMessage(null);
    setDialogOpen(true);
    setDialogLoading(true);
    
    try {
      const response = await messageService.getMessageById(messageId);
      if (response.success) {
        setSelectedMessage(response.data);
      } else {
        showLocalNotification('Failed to load message details', 'error');
      }
    } catch (err) {
      console.error('Error fetching message details:', err);
      showLocalNotification('Error loading message details', 'error');
    } finally {
      setDialogLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSendReply = async (replyText) => {
    try {
      const response = await messageService.replyToMessage(selectedMessage._id, replyText);
      
      if (response.success) {
        showLocalNotification('Reply sent successfully', 'success');
        setSelectedMessage(response.data);
        
        // Refresh the messages list to show the updated status
        fetchMessages();
      } else {
        showLocalNotification(response.error || 'Failed to send reply', 'error');
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      showLocalNotification('Error sending reply', 'error');
      throw err; // Rethrow to handle in the dialog component
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await messageService.markAllAsRead();
      if (response.success) {
        showLocalNotification('All messages marked as read', 'success');
        fetchMessages();
        fetchStats();
      } else {
        showLocalNotification(response.error || 'Failed to mark messages as read', 'error');
      }
    } catch (err) {
      console.error('Error marking messages as read:', err);
      showLocalNotification('Error marking messages as read', 'error');
    }
  };

  const handleComposeClick = () => {
    // Navigate to the correct compose URL based on the user role context
    if (isStaffPortal) {
      navigate('/staff/messages/compose');
    } else {
      navigate('/admin/messages/compose');
    }
  };
  
  // Loading skeleton for message list
  const MessageSkeleton = () => (
    <Box sx={{ width: '100%' }}>
      {[1, 2, 3, 4, 5].map((item) => (
        <Box key={item} sx={{ display: 'flex', p: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box sx={{ width: '100%' }}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="90%" height={20} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Skeleton variant="text" width="30%" height={16} />
              <Skeleton variant="text" width="20%" height={16} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );

  // Stats card component with animation
  const StatCard = ({ icon, label, value, color, delay }) => (
    <Grow in={showContent} timeout={1000} style={{ transformOrigin: '0 0 0', transitionDelay: `${delay}ms` }}>
      <Card 
        raised
        sx={{ 
          height: '100%',
          borderRadius: 2,
          background: `linear-gradient(135deg, ${alpha(theme.palette[color].light, 0.2)} 0%, ${alpha(theme.palette[color].main, 0.4)} 100%)`,
          backdropFilter: 'blur(10px)',
          border: `1px solid ${alpha(theme.palette[color].main, 0.1)}`,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: theme.shadows[10],
            background: `linear-gradient(135deg, ${alpha(theme.palette[color].light, 0.3)} 0%, ${alpha(theme.palette[color].main, 0.5)} 100%)`,
          }
        }}
      >
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette[color].dark }}>
              {value}
            </Typography>
            <Avatar sx={{ bgcolor: theme.palette[color].main, boxShadow: theme.shadows[2] }}>
              {icon}
            </Avatar>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {label}
          </Typography>
        </CardContent>
      </Card>
    </Grow>
  );
  
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header with animation */}
      <Fade in={showContent} timeout={800}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold', 
            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Message Center
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading || refreshing}
              sx={{ borderRadius: 2 }}
            >
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button 
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleComposeClick}
              sx={{ borderRadius: 1 }}
            >
              Compose
            </Button>
          </Box>
        </Box>
      </Fade>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard 
            icon={<EmailIcon />} 
            label="Total Messages" 
            value={stats.total} 
            color="primary"
            delay={100}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard 
            icon={<MarkEmailReadIcon />} 
            label="Unread Messages" 
            value={stats.unread} 
            color="error"
            delay={200}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard 
            icon={<CampaignIcon />} 
            label="Broadcast Messages" 
            value={stats.broadcast} 
            color="info"
            delay={300}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard 
            icon={<EmailIcon />} 
            label="Direct Messages" 
            value={stats.direct} 
            color="success"
            delay={400}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <StatCard 
            icon={<DashboardIcon />} 
            label="Messages Today" 
            value={stats.today} 
            color="warning"
            delay={500}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Grow in={showContent} timeout={1000} style={{ transformOrigin: '0 0 0', transitionDelay: '700ms' }}>
            <Card 
              raised
              sx={{ 
                height: '100%',
                borderRadius: 2,
                background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.4)} 100%)`,
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: theme.shadows[10],
                  background: `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.3)} 0%, ${alpha(theme.palette.secondary.main, 0.5)} 100%)`,
                }
              }}
              onClick={handleMarkAllAsRead}
            >
              <CardContent sx={{ 
                p: 2, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer'
              }}>
                <Tooltip title="Mark All as Read">
                  <Avatar sx={{ bgcolor: theme.palette.secondary.main, boxShadow: theme.shadows[2], mb: 1 }}>
                    <DoneAllIcon />
                  </Avatar>
                </Tooltip>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  Mark All as Read
                </Typography>
              </CardContent>
            </Card>
          </Grow>
        </Grid>
      </Grid>
      
      {/* Main content */}
      <Zoom in={showContent} timeout={1000}>
        <Paper 
          elevation={3}
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: theme.shadows[3],
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          {/* Filters and Search */}
          <Box sx={{ 
            px: 2, 
            pt: 2, 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' }, 
            justifyContent: 'space-between',
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterListIcon sx={{ mr: 1, color: 'text.secondary' }} />
              <Tabs 
                value={filter} 
                onChange={handleFilterChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ 
                  '& .MuiTab-root': { 
                    minWidth: 'auto',
                    borderRadius: '20px',
                    mx: 0.5,
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }
                }}
              >
                <Tab 
                  value="all" 
                  label="All" 
                  icon={<EmailIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="unread" 
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      Unread
                      {stats.unread > 0 && (
                        <Chip 
                          label={stats.unread} 
                          size="small" 
                          color="error" 
                          sx={{ ml: 1, height: 20, minWidth: 20 }} 
                        />
                      )}
                    </Box>
                  }
                  icon={<MarkEmailReadIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="broadcast" 
                  label="Broadcast" 
                  icon={<CampaignIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="direct" 
                  label="Direct" 
                  icon={<EmailIcon />}
                  iconPosition="start"
                />
                <Tab 
                  value="sent" 
                  label={
                    <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                      Sent Items
                      {stats.sent > 0 && (
                        <Chip 
                          label={stats.sent} 
                          size="small" 
                          color="secondary" 
                          sx={{ ml: 1, height: 20, minWidth: 20 }} 
                        />
                      )}
                    </Box>
                  }
                  icon={<SendIcon />}
                  iconPosition="start"
                />
              </Tabs>
            </Box>
            
            <TextField
              placeholder="Search messages..."
              size="small"
              value={searchTerm}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  '&:hover': {
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                  },
                  transition: 'all 0.2s ease-in-out',
                }
              }}
              sx={{ 
                width: { xs: '100%', md: '300px' },
                transition: 'all 0.3s ease-in-out',
                '&:focus-within': {
                  width: { md: '350px' },
                }
              }}
            />
          </Box>
          
          <Divider sx={{ mt: 2 }} />
          
          {/* Message List */}
          {loading ? (
            <MessageSkeleton />
          ) : error ? (
            <Box sx={{ textAlign: 'center', p: 4, color: 'error.main' }}>
              <Typography>{error}</Typography>
              <Button 
                variant="outlined" 
                color="primary" 
                sx={{ mt: 2 }}
                onClick={() => fetchMessages()}
              >
                Try Again
              </Button>
            </Box>
          ) : messages.length === 0 ? (
            <Box sx={{ 
              textAlign: 'center', 
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              color: 'text.secondary'
            }}>
              <Fade in={true} timeout={1000}>
                <EmailIcon sx={{ fontSize: 60, opacity: 0.5, mb: 2 }} />
              </Fade>
              <Typography variant="h6">No messages found</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 400, mx: 'auto' }}>
                {searchTerm 
                  ? `No messages match your search "${searchTerm}"`
                  : filter === 'all' 
                    ? "Your inbox is empty. Create a new message to get started."
                    : `No ${filter} messages found.`
                }
              </Typography>
              {searchTerm && (
                <Button 
                  variant="outlined" 
                  color="primary"
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 1 }}
                >
                  Clear Search
                </Button>
              )}
            </Box>
          ) : (
            <List sx={{ width: '100%', p: 0 }}>
              {messages.map((message, index) => (
                <motion.div
                  key={message._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <ListItem 
                    alignItems="flex-start"
                    sx={{ 
                      transition: 'all 0.2s ease-in-out',
                      borderLeft: !message.isRead ? `4px solid ${theme.palette.error.main}` : 'none',
                      pl: !message.isRead ? 2 : 3, // Less padding when there's a border
                      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                      position: 'relative',
                      overflow: 'hidden',
                      bgcolor: !message.isRead ? alpha(theme.palette.error.main, 0.05) : 'transparent',
                      '&:hover': { 
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        '& .MuiListItemSecondaryAction-root': {
                          opacity: 1,
                          transform: 'translateX(0)'
                        }
                      },
                      px: 3,
                      py: 2,
                      cursor: 'pointer'
                    }}
                    onClick={() => handleOpenMessage(message._id)}
                  >
                    <ListItemAvatar>
                      <Badge
                        overlap="circular"
                        badgeContent={!message.isRead ? '!' : null}
                        color="error"
                      >
                        <Avatar sx={{ 
                          bgcolor: message.isBroadcast 
                            ? theme.palette.info.main 
                            : (message.isFromMember 
                              ? theme.palette.warning.main 
                              : theme.palette.primary.main),
                          boxShadow: theme.shadows[2]
                        }}>
                          {message.isBroadcast ? <CampaignIcon /> : <EmailIcon />}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                          <Typography 
                            component="span" 
                            variant="subtitle1"
                            sx={{ 
                              fontWeight: !message.isRead ? 'bold' : 'normal',
                              color: !message.isRead ? 'text.primary' : 'text.secondary' 
                            }}
                          >
                            {message.subject}
                          </Typography>
                          {message.isBroadcast && (
                            <Chip
                              label="Broadcast"
                              size="small"
                              color="info"
                              sx={{ height: 24, borderRadius: 12 }}
                            />
                          )}
                          {message.isFromMember && (
                            <Chip
                              label="From Member"
                              size="small"
                              color="warning"
                              sx={{ height: 24, borderRadius: 12 }}
                            />
                          )}
                          {!message.isRead && (
                            <Chip
                              label="New"
                              size="small"
                              color="error"
                              sx={{ height: 24, borderRadius: 12 }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <React.Fragment>
                          <Typography
                            component="span"
                            variant="body2"
                            color={!message.isRead ? 'text.primary' : 'text.secondary'}
                            sx={{ 
                              display: 'inline',
                              fontWeight: !message.isRead ? 500 : 'normal',
                            }}
                          >
                            {message.text.length > 100 
                              ? message.text.substring(0, 100) + '...'
                              : message.text}
                          </Typography>
                          <Box sx={{ 
                            mt: 1, 
                            display: 'flex', 
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                mr: 2
                              }}
                            >
                              {message.isBroadcast 
                                ? 'Sent to all members'
                                : (message.isFromMember 
                                  ? `From: ${message.senderId?.firstName || ''} ${message.senderId?.lastName || ''}`
                                  : `To: ${message.receiverId?.userId?.firstName || ''} ${message.receiverId?.userId?.lastName || ''}`)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDistance(new Date(message.createdAt), new Date(), { addSuffix: true })}
                            </Typography>
                          </Box>
                        </React.Fragment>
                      }
                    />
                    <ListItemSecondaryAction sx={{ 
                      opacity: { xs: 1, md: 0 },
                      transform: { xs: 'translateX(0)', md: 'translateX(10px)' },
                      transition: 'all 0.2s ease-in-out',
                    }}>
                      <Tooltip title="Delete Message">
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin/messages/${message._id}`);
                          }}
                          sx={{ color: theme.palette.grey[500] }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </motion.div>
              ))}
            </List>
          )}
          
          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, bgcolor: alpha(theme.palette.background.paper, 0.5) }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
                sx={{ 
                  '& .MuiPaginationItem-root': { 
                    borderRadius: 1,
                    mx: 0.2,
                    transition: 'all 0.2s ease-in-out',
                  },
                  '& .Mui-selected': {
                    fontWeight: 'bold',
                    background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                    color: 'white',
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }
                }}
              />
            </Box>
          )}
        </Paper>
      </Zoom>
      
      {/* Message Dialog */}
      <MessageDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        message={selectedMessage}
        loading={dialogLoading}
        onReply={handleSendReply}
      />
      
      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: theme.shadows[3]
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminMessagesPage;